import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Scissors, Lock, Eye, EyeOff, Check, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Missing or invalid reset link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="reset-password-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center">
            <Scissors className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div className="text-base font-semibold tracking-tight text-zinc-900 leading-none">BreakItDown</div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Reset password</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-none text-zinc-900 mb-8">
            Choose a new password.
          </h1>

          {done ? (
            <div className="border border-emerald-300 bg-emerald-50 px-4 py-4 flex items-start gap-3" data-testid="reset-success">
              <Check className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" strokeWidth={2} />
              <div>
                <p className="text-sm font-semibold text-emerald-900">Password updated.</p>
                <p className="text-xs text-emerald-800 mt-1">Redirecting to sign in…</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="border border-zinc-300 focus-within:border-[#002FA7] flex items-center">
                <span className="px-3 text-zinc-400"><Lock className="w-4 h-4" strokeWidth={1.5} /></span>
                <input
                  type={show ? "text" : "password"}
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (min 6 chars)"
                  className="flex-1 h-12 outline-none text-sm pr-1 bg-transparent"
                  data-testid="reset-password-input"
                  autoFocus
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="px-3 text-zinc-400 hover:text-zinc-900" aria-label="Toggle password visibility" data-testid="reset-password-toggle">
                  {show ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </div>
              <div className="border border-zinc-300 focus-within:border-[#002FA7] flex items-center">
                <span className="px-3 text-zinc-400"><Lock className="w-4 h-4" strokeWidth={1.5} /></span>
                <input
                  type={show ? "text" : "password"}
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  className="flex-1 h-12 outline-none text-sm pr-3 bg-transparent"
                  data-testid="reset-confirm-input"
                />
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 flex items-start gap-2" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} /> {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || !token}
                className="w-full h-12 bg-zinc-900 hover:bg-[#002FA7] disabled:opacity-50 text-white text-sm font-medium uppercase tracking-[0.12em] transition-colors"
                data-testid="reset-submit"
              >
                {busy ? "Updating..." : "Update password"}
              </button>

              <p className="text-sm text-zinc-500 text-center pt-3">
                <Link to="/login" className="text-zinc-900 font-medium underline-offset-4 hover:underline">Back to sign in</Link>
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
